/**
 * PassportPage — the public Social Capital Passport for one user.
 *
 * Route: /passport/:handle   (public)
 *        /passport/me        (redirects to your own handle)
 *
 * Assembles three parallel API reads:
 *   /api/social-capital/portfolio/{handle}
 *   /api/badges/{handle}
 *   /api/endorsements/received/{handle}
 *
 * Sections
 *   1. Hero      — name, avatar, level, total social capital
 *   2. Categories — 11-bucket grid with bars vs the user's max
 *   3. Year-over-year — this year vs last year, per category
 *   4. Badges     — earned + a rail of catalog "locked" cards
 *   5. Endorsements — community-authored recognition wall
 *   6. Recent activity — last N contributions
 *
 * If a viewer other than the passport owner is signed in, a
 * "Say thanks" button opens the endorsement modal.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Award, Loader2, Heart, ScrollText, Feather, ArrowRight,
  Send, X, TreeDeciduous, BookOpen, Crown, HandHeart, Recycle, Eye, Gift,
  ShieldCheck, Share2, Copy, Check, FileDown,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";
import PassportShareBar from "@/components/PassportShareBar";
import PublicImpactTicker from "@/components/PublicImpactTicker";

/* ------------------------------------------------------------------ */
/*  Static category display metadata (aligned to the 8-bucket poster)  */
/* ------------------------------------------------------------------ */
const CATEGORY_META = {
  help_people:      { label: "Help People",       color: "#10B981" },
  save_food:        { label: "Save Food",         color: "#84CC16" },
  protect_nature:   { label: "Protect Nature",    color: "#EAB308" },
  support_farmers:  { label: "Support Farmers",   color: "#F97316" },
  share_knowledge:  { label: "Share Knowledge",   color: "#EF4444" },
  reuse_give:       { label: "Reuse & Give",      color: "#A855F7" },
  volunteer_serve:  { label: "Volunteer & Serve", color: "#3B82F6" },
  build_community:  { label: "Build Community",   color: "#14B8A6" },
};

// Legacy category names appear on pre-2026-03 documents until the
// migration script runs; keep an alias table so we always render into
// the canonical bucket rather than an "unknown" pill.
const CATEGORY_ALIAS = {
  education: "share_knowledge", nature: "protect_nature",
  volunteer: "volunteer_serve", career: "help_people",
  innovation: "share_knowledge", arts: "share_knowledge",
  health: "help_people", culture: "share_knowledge",
  family: "help_people", community: "build_community", food: "save_food",
};
const normalizeCat = (k) => CATEGORY_ALIAS[k] || k;

const CATEGORY_ORDER = [
  "help_people", "save_food", "protect_nature", "support_farmers",
  "share_knowledge", "reuse_give", "volunteer_serve", "build_community",
];

const ICONS = {
  TreeDeciduous, BookOpen, Crown, ScrollText, HandHeart, Award, Heart,
  Recycle, Eye, Gift, Feather, Sparkles,
};

const TIER_STYLE = {
  bronze: { bg: "linear-gradient(135deg,#f6ad55,#c05621)", ring: "#c05621" },
  silver: { bg: "linear-gradient(135deg,#cbd5e1,#64748b)", ring: "#64748b" },
  gold:   { bg: "linear-gradient(135deg,#fde68a,#b45309)", ring: "#b45309" },
};

/* ------------------------------------------------------------------ */
/*  Endorsement modal — inline; small enough not to earn its own file  */
/* ------------------------------------------------------------------ */
function EndorseModal({ open, onClose, target, onSaved }) {
  const [category, setCategory] = useState("help_people");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  // Endorse-deeplink — a giver can attach the endorsement to one specific
  // contribution. Rows are loaded lazily whenever the category changes so
  // the picker only shows entries in the bucket the giver has selected.
  const [contribs, setContribs] = useState([]);
  const [contribBusy, setContribBusy] = useState(false);
  const [selectedContribId, setSelectedContribId] = useState("");

  useEffect(() => {
    if (!open || !target?.handle && !target?.id) return;
    const who = target.handle || target.id;
    setContribBusy(true);
    setSelectedContribId("");
    const params = new URLSearchParams({ limit: "20" });
    if (category) params.set("category", category);
    http.get(`/social-capital/contributions/${encodeURIComponent(who)}?${params.toString()}`)
      .then((r) => setContribs(r.data?.items || []))
      .catch(() => setContribs([]))
      .finally(() => setContribBusy(false));
  }, [open, category, target?.handle, target?.id]);

  if (!open) return null;

  const submit = async () => {
    if (message.trim().length < 4) {
      toast.error("Say a few words about what they did.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        to: target.handle || target.id,
        category,
        message: message.trim(),
      };
      // Attach the selected contribution as a source_ref so downstream
      // consumers (Wall of Thanks, notifications, exports) can deep-link
      // right to the action being praised.
      if (selectedContribId) {
        payload.source_ref = { type: "point_event", id: selectedContribId };
      }
      const r = await http.post("/endorsements", payload);
      toast.success("Thanks sent.");
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send endorsement.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
         data-testid="endorse-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="overline" style={{ color: "#059669" }}>Recognize a contribution</div>
            <h3 className="font-display text-2xl leading-tight">
              Say thanks to <span style={{ color: "#059669" }}>{target.name}</span>
            </h3>
          </div>
          <button onClick={onClose} data-testid="endorse-modal-close" aria-label="Close">
            <X className="w-5 h-5 opacity-60" />
          </button>
        </div>
        <label className="text-xs uppercase tracking-wider opacity-70">Category</label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="endorse-category"
          className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
        >
          {CATEGORY_ORDER.map((k) => (
            <option key={k} value={k}>{CATEGORY_META[k]?.label || k}</option>
          ))}
        </select>

        {/* Endorse-deeplink — pick the specific contribution */}
        <label className="text-xs uppercase tracking-wider opacity-70 mt-4 block">
          Which contribution? <span className="opacity-60 normal-case tracking-normal">(optional)</span>
        </label>
        <div
          className="mt-1 rounded-lg border max-h-52 overflow-y-auto"
          style={{ borderColor: "rgba(0,0,0,0.12)" }}
          data-testid="endorse-contrib-picker"
        >
          <button
            type="button"
            onClick={() => setSelectedContribId("")}
            className={`w-full text-left px-3 py-2 text-sm border-b transition-colors ${!selectedContribId ? "font-semibold" : "hover:bg-emerald-50"}`}
            style={{
              background: !selectedContribId ? "#ECFDF5" : "transparent",
              color: !selectedContribId ? "#065F46" : "#0F172A",
              borderColor: "rgba(0,0,0,0.06)",
            }}
            data-testid="endorse-contrib-any"
          >
            <div>General thanks — not tied to one action</div>
            <div className="text-[11px] opacity-60 mt-0.5">Best when you want to recognise their overall work in this category</div>
          </button>
          {contribBusy ? (
            <div className="px-3 py-3 text-xs opacity-60 inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading contributions…
            </div>
          ) : contribs.length === 0 ? (
            <div className="px-3 py-3 text-xs opacity-60" data-testid="endorse-contrib-empty">
              No verified contributions in this category yet.
            </div>
          ) : (
            contribs.map((c) => {
              const active = selectedContribId === c.id;
              return (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => setSelectedContribId(c.id)}
                  className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 transition-colors ${active ? "font-semibold" : "hover:bg-emerald-50"}`}
                  style={{
                    background: active ? "#ECFDF5" : "transparent",
                    color: active ? "#065F46" : "#0F172A",
                    borderColor: "rgba(0,0,0,0.06)",
                  }}
                  data-testid={`endorse-contrib-${c.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="truncate">{c.reason || "Contribution"}</span>
                    <span className="ml-auto text-[11px] tabular-nums opacity-70">{c.points} pts</span>
                  </div>
                  <div className="text-[11px] opacity-60 mt-0.5">
                    {(c.created_at || "").slice(0, 10)}
                    {c.verified ? " · verified" : ""}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <label className="text-xs uppercase tracking-wider opacity-70 mt-4 block">
          Message ({message.length}/600)
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 600))}
          data-testid="endorse-message"
          placeholder="What did they do that mattered?"
          rows={4}
          className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-full border">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="endorse-submit"
            className="px-5 py-2 text-sm rounded-full text-white inline-flex items-center gap-2"
            style={{ background: "#059669", opacity: busy ? 0.6 : 1 }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send thanks
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */
export default function PassportPage() {
  const { handle: rawHandle } = useParams();
  const navigate = useNavigate();
  const { user: viewer, loading: authLoading } = useAuth() || {};

  // /passport/me → redirect to /passport/<own-handle>. Must wait for
  // AuthContext hydration; otherwise viewer===null during the initial
  // GET /api/auth/me fires a false unauthenticated redirect.
  useEffect(() => {
    if (rawHandle !== "me") return;
    if (authLoading) return;
    if (!viewer) { navigate("/login", { replace: true }); return; }
    if (viewer.handle) navigate(`/passport/${viewer.handle}`, { replace: true });
    else navigate(`/passport/${viewer.id}`, { replace: true });
  }, [rawHandle, viewer, authLoading, navigate]);

  const handle = rawHandle === "me" ? null : (rawHandle || "").replace(/^@/, "");

  const [portfolio, setPortfolio] = useState(null);
  const [badges, setBadges] = useState(null);
  const [endorsements, setEndorsements] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [endorseOpen, setEndorseOpen] = useState(false);
  const [eventCat, setEventCat] = useState("all");
  const [eventSite, setEventSite] = useState("all");
  // SpLink.pro — shareable short URL + click count for this passport.
  // Lazily minted on first fetch; the "viewed N times" badge only
  // renders once the API has returned real numbers so the hero doesn't
  // flash a "0 views" state that instantly becomes wrong.
  const [shortlink, setShortlink] = useState(null);
  const [shortlinkStats, setShortlinkStats] = useState(null);
  const [copyState, setCopyState] = useState("idle"); // idle | ok

  useEffect(() => {
    if (!handle) return;
    let dead = false;
    setLoading(true); setNotFound(false);
    setShortlink(null); setShortlinkStats(null);
    Promise.all([
      http.get(`/social-capital/portfolio/${encodeURIComponent(handle)}`).catch(() => null),
      http.get(`/badges/${encodeURIComponent(handle)}`).catch(() => null),
      http.get(`/endorsements/received/${encodeURIComponent(handle)}`).catch(() => null),
      http.get(`/badges/catalog`).catch(() => null),
    ]).then(([p, b, e, c]) => {
      if (dead) return;
      if (!p?.data) { setNotFound(true); setLoading(false); return; }
      setPortfolio(p.data);
      setBadges(b?.data || { items: [] });
      setEndorsements(e?.data || { items: [] });
      setCatalog(c?.data || { items: [] });
      setLoading(false);
    });
    // SpLink — deferred (non-blocking) fetch. Minting the shortlink on
    // demand keeps the passport hero fast even when SpLink is slow.
    http.get(`/splink/v1/passport/${encodeURIComponent(handle)}`)
      .then((r) => { if (!dead) setShortlink(r.data); })
      .catch(() => {});
    http.get(`/splink/v1/passport/${encodeURIComponent(handle)}/stats`)
      .then((r) => { if (!dead) setShortlinkStats(r.data); })
      .catch(() => {});
    return () => { dead = true; };
  }, [handle]);

  const isOwn = viewer && portfolio?.user && viewer.id === portfolio.user.id;
  const catTotals = portfolio?.category_totals || {};
  const maxCat = useMemo(
    () => Math.max(1, ...Object.values(catTotals).map((v) => Number(v) || 0)),
    [catTotals],
  );
  const yoy = portfolio?.year_over_year || {};
  const events = portfolio?.recent_events || [];
  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      const c = e.category || "community";
      if (eventCat !== "all" && c !== eventCat) return false;
      if (eventSite !== "all" && (e.site_source || "") !== eventSite) return false;
      return true;
    });
  }, [events, eventCat, eventSite]);
  const earnedCodes = new Set((badges?.items || []).map((b) => b.code));

  if (rawHandle === "me") return null; // navigating
  if (loading) {
    return (
      <div className="max-w-3xl mx-auto py-24 text-center" data-testid="passport-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-70" />
        <div className="mt-3 text-sm opacity-60">Loading the passport…</div>
      </div>
    );
  }
  if (notFound || !portfolio) {
    return (
      <div className="max-w-3xl mx-auto py-24 text-center" data-testid="passport-notfound">
        <h2 className="font-display text-3xl mb-2">No passport yet</h2>
        <p className="text-sm opacity-70">We couldn&apos;t find a Social Capital Passport for <code>@{handle}</code>.</p>
        <Link to="/why-we-build" className="mt-6 inline-block underline underline-offset-4 text-sm" style={{ color: "#059669" }}>
          Read what a passport is →
        </Link>
      </div>
    );
  }

  const u = portfolio.user || {};
  const totalPts = u.social_capital || 0;
  const level = u.level || "Helper";
  const displayName = u.name || u.handle || "Anonymous";
  const initials = displayName.trim().charAt(0).toUpperCase();

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="passport-page">
      <SeoHead
        section="passport"
        title={`${displayName}'s Social Capital Passport`}
        description={`${totalPts.toLocaleString()} pts · ${level} · a lifetime record of ${displayName}'s contributions.`}
        url={`/passport/${u.handle || u.id}`}
        image={`${process.env.REACT_APP_SOCIAL_CAPITAL_API_BASE || process.env.REACT_APP_BACKEND_URL || ""}/api/social-capital/og/${encodeURIComponent(u.handle || u.id)}.png`}
      />

      {/* Live network momentum — reminds returning members their own
          entries contribute to the global counters. Cheap, refetches
          every 60s. */}
      <PublicImpactTicker />

      {/* Hero */}
      <section
        className="relative overflow-hidden rounded-3xl p-6 sm:p-10 mb-8"
        style={{
          background: "linear-gradient(135deg,#065f46 0%,#059669 55%,#10b981 100%)",
          color: "#F0FDF4",
        }}
        data-testid="passport-hero"
      >
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-25 pointer-events-none"
             style={{ background: "radial-gradient(circle,#A7F3D0 0%,transparent 70%)" }} />
        <div className="relative z-10 flex flex-wrap items-center gap-6">
          <div
            className="w-24 h-24 rounded-full inline-flex items-center justify-center text-3xl font-display font-bold shrink-0"
            style={{ background: "#F0FDF4", color: "#065f46" }}
            aria-hidden
          >
            {initials}
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="overline text-white/70 mb-1">
              <Sparkles className="w-3 h-3 inline mr-1" /> Social Capital Passport
            </div>
            <h1 className="font-display text-3xl sm:text-5xl leading-[1.05] font-bold"
                data-testid="passport-name">
              {displayName}
            </h1>
            <div className="mt-1 text-white/80 text-sm">
              {u.handle && <span className="font-mono">@{u.handle}</span>}
              {u.profession && <> · {u.profession}</>}
              {(u.city || u.country) && <> · {[u.city, u.country].filter(Boolean).join(", ")}</>}
            </div>
            <div className="mt-4 flex flex-wrap items-baseline gap-4">
              <div>
                <div className="font-display text-5xl font-bold" data-testid="passport-total">
                  {totalPts.toLocaleString()}
                </div>
                <div className="text-xs uppercase tracking-widest opacity-70 mt-1">Lifetime pts</div>
              </div>
              <span
                className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider"
                style={{ background: "#F0FDF4", color: "#065f46" }}
                data-testid="passport-level"
              >
                {level}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {viewer && !isOwn && (
              <button
                onClick={() => setEndorseOpen(true)}
                data-testid="passport-endorse-btn"
                className="rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
                style={{ background: "#F0FDF4", color: "#065f46" }}
              >
                <Heart className="w-4 h-4" /> Say thanks
              </button>
            )}
            {shortlink?.short_url && (
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shortlink.short_url);
                    setCopyState("ok");
                    toast.success(`Copied ${shortlink.short_url}`);
                    setTimeout(() => setCopyState("idle"), 1800);
                  } catch {
                    toast.error("Could not copy — long-press to select.");
                  }
                }}
                data-testid="passport-share-btn"
                className="rounded-full px-4 py-2 text-xs font-medium border border-white/40 text-white/95 hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
                title={shortlink.short_url}
              >
                {copyState === "ok"
                  ? (<><Check className="w-3.5 h-3.5" /> Copied</>)
                  : (<><Share2 className="w-3.5 h-3.5" /> Share passport</>)}
              </button>
            )}
            <Link
              to="/why-we-build"
              className="rounded-full px-4 py-2 text-xs font-medium border border-white/40 text-white/90 hover:bg-white/10 transition-colors text-center"
              data-testid="passport-manifesto"
            >
              What is this?
            </Link>
            {/* Passport Export — JSON + PDF snapshot links. Public data,
                open in new tab; browser handles Content-Disposition. */}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/social-capital/passport-export/${encodeURIComponent(u.handle || u.id)}.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="passport-export-pdf"
              className="rounded-full px-4 py-2 text-xs font-medium border border-white/40 text-white/95 hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
              title="Download a printable PDF snapshot"
            >
              <FileDown className="w-3.5 h-3.5" /> Export PDF
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/social-capital/passport-export/${encodeURIComponent(u.handle || u.id)}.pdf?cv=1`}
              target="_blank"
              rel="noreferrer"
              data-testid="passport-export-cv"
              className="rounded-full px-4 py-2 text-xs font-medium border border-white/40 text-white/95 hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
              title="A resume-style single-pager for CV attachments — hides the raw audit trail, keeps badges + endorsements up top"
            >
              <FileDown className="w-3.5 h-3.5" /> CV PDF
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/social-capital/passport-export/${encodeURIComponent(u.handle || u.id)}.json`}
              target="_blank"
              rel="noreferrer"
              data-testid="passport-export-json"
              className="rounded-full px-4 py-2 text-xs font-medium border border-white/40 text-white/80 hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
              title="Download the raw JSON snapshot"
            >
              JSON
            </a>
          </div>
        </div>
        {/* Viewed-N-times badge — only renders when SpLink has real numbers. */}
        {shortlinkStats && shortlinkStats.clicks > 0 && (
          <div
            className="relative z-10 mt-6 inline-flex items-center gap-2 text-xs rounded-full px-3 py-1"
            style={{ background: "rgba(240,253,244,0.15)", color: "#F0FDF4" }}
            data-testid="passport-view-badge"
          >
            <Eye className="w-3.5 h-3.5" />
            <span className="font-mono font-semibold">
              {shortlinkStats.clicks.toLocaleString()}
            </span>
            <span className="opacity-80">views</span>
            {shortlinkStats.top_country && (
              <span className="opacity-70">· mostly from {shortlinkStats.top_country}</span>
            )}
          </div>
        )}
        {/* WhatsApp / LinkedIn / X / Copy — points at the crawler-ready
            /share/passport/{handle} endpoint so previews render the
            Pillow OG card automatically. */}
        <div className="relative z-10">
          <PassportShareBar handle={u.handle || u.id} name={displayName} points={totalPts} />
        </div>
      </section>

      {/* Category breakdown */}
      <section className="mb-10" data-testid="passport-categories">
        <div className="overline mb-3" style={{ color: "#059669" }}>
          Contribution by category
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {CATEGORY_ORDER.map((k) => {
            const meta = CATEGORY_META[k];
            const v = Number(catTotals[k] || 0);
            const pct = Math.round((v / maxCat) * 100);
            return (
              <div key={k}
                   className="rounded-2xl border p-4 bg-white"
                   style={{ borderColor: "rgba(0,0,0,0.08)" }}
                   data-testid={`cat-${k}`}>
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: meta.color }} className="font-semibold">{meta.label}</span>
                  <span className="font-mono opacity-70">{v}</span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full overflow-hidden bg-slate-100">
                  <div className="h-full rounded-full"
                       style={{ width: `${pct}%`, background: meta.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Year-over-year */}
      <section className="mb-10 rounded-2xl border p-6 bg-white"
               style={{ borderColor: "rgba(0,0,0,0.08)" }}
               data-testid="passport-yoy">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="overline" style={{ color: "#059669" }}>
              Year over year
            </div>
            <h2 className="font-display text-2xl">Compete with yourself.</h2>
          </div>
          <div className="text-sm font-mono">
            <span className="opacity-60 mr-2">This year</span>
            <span className="text-lg font-semibold" style={{ color: "#059669" }}>
              {(yoy.this_year || 0).toLocaleString()}
            </span>
            <span className="opacity-40 mx-2">·</span>
            <span className="opacity-60 mr-2">Last year</span>
            <span className="text-lg">{(yoy.last_year || 0).toLocaleString()}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CATEGORY_ORDER.map((k) => {
            const meta = CATEGORY_META[k];
            const cur = Number((yoy.per_category_this_year || {})[k] || 0);
            const prev = Number((yoy.per_category_last_year || {})[k] || 0);
            const max = Math.max(cur, prev, 1);
            if (cur === 0 && prev === 0) return null;
            return (
              <div key={k} data-testid={`yoy-${k}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span style={{ color: meta.color }} className="font-semibold">{meta.label}</span>
                  <span className="font-mono opacity-70">
                    <span style={{ color: meta.color }}>{cur}</span>
                    <span className="opacity-40 mx-1">/</span>
                    <span>{prev}</span>
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="h-2.5 rounded-full overflow-hidden bg-slate-100">
                    <div className="h-full rounded-full" style={{ width: `${(cur / max) * 100}%`, background: meta.color }} />
                  </div>
                  <div className="h-2 rounded-full overflow-hidden bg-slate-100 opacity-70">
                    <div className="h-full rounded-full" style={{ width: `${(prev / max) * 100}%`, background: meta.color, opacity: 0.5 }} />
                  </div>
                </div>
              </div>
            );
          })}
          {CATEGORY_ORDER.every((k) => !(yoy.per_category_this_year || {})[k] && !(yoy.per_category_last_year || {})[k]) && (
            <div className="text-sm opacity-60 col-span-2" data-testid="yoy-empty">
              No contributions logged yet this year. Every point tells a story — start one today.
            </div>
          )}
        </div>
      </section>

      {/* Badges */}
      <section className="mb-10" data-testid="passport-badges">
        <div className="overline mb-3" style={{ color: "#059669" }}>Badges</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {(catalog?.items || []).map((b) => {
            const earned = earnedCodes.has(b.code);
            const Icon = ICONS[b.icon] || Award;
            const style = TIER_STYLE[b.tier] || TIER_STYLE.bronze;
            return (
              <div
                key={b.code}
                className="rounded-2xl border p-4 bg-white flex items-start gap-3"
                style={{
                  borderColor: earned ? style.ring : "rgba(0,0,0,0.08)",
                  opacity: earned ? 1 : 0.45,
                }}
                data-testid={`badge-${b.code}`}
              >
                <div
                  className="w-12 h-12 rounded-xl inline-flex items-center justify-center text-white shrink-0"
                  style={{ background: earned ? style.bg : "#e2e8f0" }}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-sm font-semibold leading-tight">{b.name}</div>
                  <div className="text-[11px] opacity-70 mt-0.5">{b.description}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider mt-1 opacity-60">
                    {b.tier}{earned ? " · earned" : " · locked"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Endorsements */}
      <section className="mb-10 rounded-2xl border p-6"
               style={{ borderColor: "rgba(0,0,0,0.08)", background: "linear-gradient(135deg,#ECFDF5 0%,#F0FDF4 100%)" }}
               data-testid="passport-endorsements">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
          <div>
            <div className="overline" style={{ color: "#059669" }}>Community says</div>
            <h2 className="font-display text-2xl">Recognition, community-authored.</h2>
          </div>
          {viewer && !isOwn && (
            <button
              onClick={() => setEndorseOpen(true)}
              data-testid="endorsements-cta"
              className="rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-2 text-white"
              style={{ background: "#059669" }}
            >
              <Heart className="w-4 h-4" /> Add your voice
            </button>
          )}
        </div>
        {(endorsements?.items || []).length === 0 ? (
          <div className="text-sm opacity-60" data-testid="endorsements-empty">
            No endorsements yet. Reputation is built by others — invite someone to say thanks.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(endorsements.items || []).map((e) => (
              <blockquote key={e.id}
                          className="rounded-2xl bg-white/85 backdrop-blur border p-4"
                          style={{ borderColor: "rgba(5,150,105,0.15)" }}
                          data-testid={`endorsement-${e.id}`}>
                <div className="flex items-center gap-2 text-xs mb-2">
                  <span
                    className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-semibold text-white"
                    style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}
                  >
                    {(e.from_name || "?").trim().charAt(0).toUpperCase()}
                  </span>
                  <Link
                    to={e.from_handle ? `/passport/${e.from_handle}` : "#"}
                    className="font-medium hover:underline underline-offset-4"
                  >
                    {e.from_name || "Anonymous"}
                  </Link>
                  <span className="opacity-60">·</span>
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                    style={{
                      color: CATEGORY_META[e.category]?.color || "#0f172a",
                      background: `${CATEGORY_META[e.category]?.color || "#94a3b8"}18`,
                    }}
                  >
                    {CATEGORY_META[e.category]?.label || e.category}
                  </span>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: "#0f172a" }}>
                  &ldquo;{e.message}&rdquo;
                </p>
              </blockquote>
            ))}
          </div>
        )}
      </section>

      {/* Recent activity */}
      <section className="mb-10" data-testid="passport-recent">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <div className="overline" style={{ color: "#059669" }}>Recent contributions</div>
          <Link
            to={`/board/${u.handle || u.id}`}
            data-testid="passport-view-board"
            className="text-xs inline-flex items-center gap-1 rounded-full border px-3 py-1 hover:bg-black/[0.02]"
          >
            View printable board <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Timeline filter chips */}
        {events.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1.5" data-testid="timeline-filters">
            <button
              onClick={() => setEventCat("all")}
              data-testid="filter-cat-all"
              className="rounded-full border px-2.5 py-1 text-[11px] transition-colors"
              style={{
                borderColor: eventCat === "all" ? "#059669" : "rgba(0,0,0,0.15)",
                background:  eventCat === "all" ? "#059669" : "transparent",
                color:       eventCat === "all" ? "#fff" : "inherit",
              }}
            >
              All ({events.length})
            </button>
            {CATEGORY_ORDER.filter((k) => events.some((e) => (e.category || "community") === k)).map((k) => {
              const meta = CATEGORY_META[k];
              const active = eventCat === k;
              return (
                <button
                  key={k}
                  onClick={() => setEventCat(k)}
                  data-testid={`filter-cat-${k}`}
                  className="rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors"
                  style={{
                    borderColor: active ? meta.color : "rgba(0,0,0,0.15)",
                    background:  active ? meta.color : "transparent",
                    color:       active ? "#fff" : meta.color,
                  }}
                >
                  {meta.label}
                </button>
              );
            })}
            {/* Site source filters — only render if events span multiple sites */}
            {(() => {
              const sites = Array.from(new Set(events.map((e) => e.site_source).filter(Boolean)));
              if (sites.length < 2) return null;
              return (
                <>
                  <span className="w-px bg-black/10 mx-1" />
                  <button
                    onClick={() => setEventSite("all")}
                    data-testid="filter-site-all"
                    className="rounded-full border px-2.5 py-1 text-[11px] transition-colors"
                    style={{
                      borderColor: eventSite === "all" ? "#0f172a" : "rgba(0,0,0,0.15)",
                      background:  eventSite === "all" ? "#0f172a" : "transparent",
                      color:       eventSite === "all" ? "#fff" : "inherit",
                    }}
                  >
                    All sites
                  </button>
                  {sites.map((s) => {
                    const active = eventSite === s;
                    return (
                      <button
                        key={s}
                        onClick={() => setEventSite(s)}
                        data-testid={`filter-site-${s}`}
                        className="rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors"
                        style={{
                          borderColor: active ? "#0f172a" : "rgba(0,0,0,0.15)",
                          background:  active ? "#0f172a" : "transparent",
                          color:       active ? "#fff" : "inherit",
                        }}
                      >
                        {s}
                      </button>
                    );
                  })}
                </>
              );
            })()}
          </div>
        )}

        {filteredEvents.length === 0 ? (
          <div className="text-sm opacity-60" data-testid="timeline-empty">
            {events.length === 0
              ? "Nothing recorded yet."
              : "No contributions match this filter."}
          </div>
        ) : (
          <ol className="border-l-2 pl-5 space-y-3" style={{ borderColor: "rgba(5,150,105,0.25)" }}>
            {filteredEvents.slice(0, 12).map((ev) => {
              const cat = normalizeCat(ev.category || "help_people");
              const meta = CATEGORY_META[cat] || { label: cat, color: "#94a3b8" };
              return (
                <li key={ev.id} className="relative pl-2" data-testid={`event-${ev.id}`}>
                  <span
                    className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full ring-4 ring-white"
                    style={{ background: meta.color }}
                  />
                  <div className="text-sm">
                    <span className="font-mono font-semibold" style={{ color: meta.color }}>+{ev.points}</span>
                    <span className="opacity-70"> · {meta.label}</span>
                    {ev.verified && (
                      <span
                        className="inline-flex items-center gap-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 ml-1.5"
                        style={{ background: "#DCFCE7", color: "#065F46" }}
                        title="Verified by community endorsement"
                        data-testid={`event-verified-${ev.id}`}
                      >
                        <ShieldCheck className="w-2.5 h-2.5" /> Verified
                      </span>
                    )}
                    {ev.site_source && <span className="opacity-40"> · {ev.site_source}</span>}
                  </div>
                  <div className="text-xs opacity-60">{ev.reason}</div>
                  <div className="text-[10px] font-mono opacity-40">{(ev.created_at || "").slice(0, 10)}</div>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {/* Footer */}
      <div className="text-center pt-6 border-t" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
        <Link to="/why-we-build" className="text-sm underline underline-offset-4 opacity-70 hover:opacity-100"
              style={{ color: "#059669" }} data-testid="passport-footer-manifesto">
          Why this passport exists <ArrowRight className="w-3 h-3 inline" />
        </Link>
      </div>

      <EndorseModal
        open={endorseOpen}
        onClose={() => setEndorseOpen(false)}
        target={{ id: u.id, handle: u.handle, name: displayName }}
        onSaved={(saved) => setEndorsements((p) => ({
          ...(p || {}),
          items: [saved, ...((p && p.items) || [])],
          count: ((p && p.count) || 0) + 1,
        }))}
      />
    </div>
  );
}
